import {
  Button,
  Column,
  Copy,
  Row,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Flex, Heading } from '@grubhubprod/gh-cookbook-react/dist';
import { format as dateFormat, endOfDay, startOfDay } from 'date-fns';
import DateRangeSelection from 'src/components/DateRange/DateRangeSelection';
import { Input } from 'src/components/Input/Input';
import { OrderSelection } from 'src/components/enterprise/features/OrderHistory/OrderList/OrderSelection';
import { ILocation } from 'src/types/Location';
import {
  IDepartment,
  useDepartmentSearch,
  useLocationSearch,
} from 'src/utilities/useSearchHooks';
import { OrderHistoryFilters } from 'src/components/enterprise/features/OrderHistory/OrderHistory';
import { SelectSearch } from 'src/components/SelectSearch/SelectSearch';
import { Tooltip } from 'src/components/Tooltip/Tooltip';

interface filterParams {
  filters: OrderHistoryFilters;
  searchParams: SearchFormParams;
}
export interface FiltersProps {
  corpId: string;
  onFilterChange: ({ filters, searchParams }: filterParams) => void;
  onSearchParamsChange: (searchParams: SearchFormParams) => void;
  availableOrderTypes: string[];
  shouldDisplayDepartments: boolean;
  shouldDisplayLocations: boolean;
  filters?: OrderHistoryFilters;
}
interface SearchFormParams {
  query: string;
}
const DATE_FORMAT = 'MMM d';

export const Filters = forwardRef(
  (
    {
      corpId,
      onFilterChange,
      onSearchParamsChange,
      filters = {},
      availableOrderTypes,
      shouldDisplayDepartments,
      shouldDisplayLocations,
    }: FiltersProps,
    ref
  ): JSX.Element => {
    const { register, handleSubmit, reset, getValues } =
      useForm<SearchFormParams>();
    const { start, end, order_types } = filters;
    const selectDepartmentSearchRef = useRef(null);
    const selectLocationSearchRef = useRef(null);

    useImperativeHandle(ref, () => ({
      reset: () => resetFilters(),
    }));

    function resetFilters() {
      if (selectDepartmentSearchRef.current) {
        selectDepartmentSearchRef.current.reset();
      }
      if (selectLocationSearchRef.current) {
        selectLocationSearchRef.current.reset();
      }
      reset();
    }

    function getDateRangeText(): string {
      return start && end
        ? `${dateFormat(new Date(start), DATE_FORMAT)} - ${dateFormat(
            new Date(end),
            DATE_FORMAT
          )}`
        : 'Date range';
    }

    async function onDateRangeApplied(
      currentStartDate: Date,
      currentEndDate: Date
    ) {
      onHandleFilterChange({
        start: currentStartDate && startOfDay(currentStartDate).toISOString(),
        end: currentEndDate && endOfDay(currentEndDate).toISOString(),
      });
    }

    function onHandleFilterChange(filters: Partial<OrderHistoryFilters>) {
      onFilterChange({ filters, searchParams: { query: getValues('query') } });
    }

    return (
      <Row data-testid="order-history-filters">
        <Column phone={4}>
          <Row gutter={8} as="form">
            <Tooltip
              offset={[0, 10]}
              maxWidth="290px"
              roundedContainer
              content={<TipsTooltip />}
            >
              <Column gutter={8} phone={8}>
                <Input
                  ariaLabel="query"
                  name="query"
                  id="query"
                  ref={register}
                  placeholder="Employee name, email, expense code"
                  prefixIcon={
                    <span>
                      <Copy variant="body" color="secondaryText">
                        <Flex alignItems="center">
                          <SvgMagnifyingGlass size="16px" />
                        </Flex>
                      </Copy>
                    </span>
                  }
                  onChange={() => onSearchParamsChange}
                />
              </Column>
            </Tooltip>
            <Column gutter={8} phone={4}>
              <Button
                variant="primary"
                block
                buttonSize="medium"
                onClick={handleSubmit(onSearchParamsChange)}
              >
                Search
              </Button>
            </Column>
          </Row>
        </Column>
        <Column phone={8}>
          <Flex justifyContent="flex-end">
            <Stack x="sm" width="150px">
              <DateRangeSelection
                useInputStyle
                label={getDateRangeText()}
                startDateValue={start && new Date(start)}
                endDateValue={end && new Date(end)}
                onApply={onDateRangeApplied}
              />
            </Stack>
            <Stack x="sm">
              <OrderSelection
                availableOrderTypes={availableOrderTypes}
                selectedOrderTypes={order_types}
                onSelectedOrderTypesChange={(order_types) =>
                  onHandleFilterChange({
                    order_types,
                  })
                }
              />
            </Stack>
            {shouldDisplayDepartments && (
              <Stack x="sm" width="180px" data-testid="department-filter">
                <SelectSearch<IDepartment, true>
                  ref={selectDepartmentSearchRef}
                  isMultiSelect
                  corpId={corpId}
                  maxHeight="350px"
                  displayProp="name"
                  keyName="id"
                  selectedItemLabel="department"
                  selectedItemPluralLabel="departments"
                  placeholder="Departments"
                  searchResource={useDepartmentSearch}
                  onValueChange={(departments) => {
                    onHandleFilterChange({
                      departments: departments.map(({ name }) => name),
                    });
                  }}
                />
              </Stack>
            )}
            {shouldDisplayLocations && (
              <Stack x="sm" width="180px" data-testid="location-filter">
                <SelectSearch<ILocation, true>
                  ref={selectLocationSearchRef}
                  isMultiSelect
                  corpId={corpId}
                  maxHeight="350px"
                  displayProp="name"
                  keyName="id"
                  selectedItemLabel="location"
                  selectedItemPluralLabel="locations"
                  placeholder="Locations"
                  searchResource={useLocationSearch}
                  onValueChange={(locations = []) => {
                    onHandleFilterChange({
                      locations: locations.map(({ name }) => name),
                    });
                  }}
                />
              </Stack>
            )}
          </Flex>
        </Column>
      </Row>
    );
  }
);

Filters.displayName = 'Filters';

function TipsTooltip() {
  const filterList = [
    'Name or email of the person who placed the order',
    'Name or email of the person allocated to an order',
    'Expense code',
    'Name of restaurant',
    'Name of scheduled team order',
  ];

  return (
    <Copy variant="body" color="invertedPrimaryText">
      <Stack y="sm">
        <Heading variant="h4">Tips for searching</Heading>
      </Stack>
      <Stack y="sm">You can find an order by:</Stack>
      <Stack y="sm" as="ul">
        {filterList.map((filter) => (
          <Stack key={filter} y="xx-sm" as="li">
            {filter}
          </Stack>
        ))}
      </Stack>
    </Copy>
  );
}
